import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AdminService } from 'src/app/service/admin.service';
import { UserService } from 'src/app/service/user.service';
import { EventsService } from 'src/app/service/events.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { User } from 'src/app/model/user';
import { Submission, Review } from '../../model/paper';
import { Event } from '../../model/event';
import { NavbarService } from 'src/app/service/navbar.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin, Observable } from 'rxjs';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { AssignReview } from 'src/app/model/assign.review';
import { InfoService } from 'src/app/service/info.service';
import { ReviewsService } from 'src/app/service/reviews.service';
import { AuthService } from 'src/app/service/auth.service';
import { NotificationService } from 'src/app/service/notification.service';
import { EventTopic } from 'src/app/model/eventTopic';
import { TimezoneService } from 'src/app/service/timezone.service';
import { CountryPipe } from 'src/app/pipe/country.pipe';

export class UserReviewerSubmission extends User {
  isUserReviewer: boolean;
}

@Component({
  selector: 'app-find-reviewers-by-interest',
  templateUrl: './find-reviewers-by-interest.component.html',
  styleUrls: ['./find-reviewers-by-interest.component.scss'],
  providers: [CountryPipe]
})
export class FindReviewersByInterestComponent implements OnInit {
  displayedColumns: string[] = ['select', 'user', 'reviews', 'topics', 'claimed', 'claimed-assigned', 'affiliation'];
  claimSelection = new SelectionModel<User>(true, []);
  findSelection = new SelectionModel<User>(true, []);

  userClaimsDataSource = new MatTableDataSource<UserReviewerSubmission>();
  usersByInterestDataSource = new MatTableDataSource<UserReviewerSubmission>();

  public submission: Submission;
  public event: Event;
  public reviewsArray = new Array();
  public submissionTopicsArray = new Array();
  public topicsArray = new Array();
  newReview: AssignReview;
  reviewConfiguration: EventReviewConfiguration;
  form: FormGroup;
  public userIsChair = false;
  reviews: Array<Review>;

  link;

  constructor(
    private authService: AuthService,
    private navbar: NavbarService,
    private route: ActivatedRoute,
    public reviewService: ReviewsService,
    public infoService: InfoService,
    private eventsService: EventsService,
    private reviewConfigurationService: ReviewsConfigurationService,
    private submissionsService: SubmissionsService,
    private notificationService: NotificationService,
    private fb: FormBuilder,
    private tzService: TimezoneService,
    private countryPipe: CountryPipe,
    // Quando aberto como DIÁLOGO (a partir de "Adicionar revisor"), recebe o id
    // da submissão via data e usa o dialogRef p/ fechar ao atribuir. Como PÁGINA
    // (rota), ambos são null e o id vem da rota.
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { submissionId?: number } | null,
    @Optional() public dialogRef: MatDialogRef<FindReviewersByInterestComponent>
  ) { }

  // Total de candidatos selecionados nas duas listas (bid + interesse).
  get selectedCount(): number {
    return this.claimSelection.selected.length + this.findSelection.selected.length;
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.getReviewers();
    });
  }

  getReviewers() {
    if (this.dialogData?.submissionId != null) {
      this.loadReviewers(this.dialogData.submissionId);
    } else {
      this.route.params.subscribe(({ id }) => this.loadReviewers(id));
    }
  }

  private loadReviewers(id): void {
    this.submissionsService.getSubmissionReviewersByInterest(id).subscribe(submission => {
        this.submission = submission;

        const userClaim: Array<UserReviewerSubmission> = submission.userClaims.map(u => {
          const user = u as UserReviewerSubmission;
          user.isUserReviewer = submission.isReviewer(user.id);
          return user;
        });

        const userInterest: Array<UserReviewerSubmission> = submission.usersByInterest.map(u => {
          const user = u as UserReviewerSubmission;
          user.isUserReviewer = submission.isReviewer(user.id);
          return user;
        });

        this.userClaimsDataSource.data = userClaim;
        this.usersByInterestDataSource.data = userInterest;
        this.submissionTopicsArray.push(...submission.topics.map(t => t.name));

        // Um ÚNICO getEventByTrack (antes eram dois em série); config + isChair
        // resolvem em PARALELO via forkJoin — menos round-trips encadeados.
        this.eventsService.getEventByTrack(submission.trackID).subscribe(event => {
          this.event = event;
          this.submission.event = event;
          this.link = {
            label: 'forms.buttons.assign-review-date',
            route: `/admin/${event.id}/reviews/configuration`,
            align: 'end'
          };
          forkJoin([
            this.reviewConfigurationService.getReviewsConfiguration(event.id),
            this.authService.directUserIsChair(event.id),
            this.authService.directUserIsAdmin()
          ]).subscribe(([rConfiguration, isChair, isAdmin]) => {
            this.reviewConfiguration = rConfiguration;
            this.initForm();
            // Admin do sistema (superuser) tem as permissões de chair em qualquer evento.
            this.userIsChair = isAdmin || isChair;
          });
        });

        this.navbar.loadingStop();
      });
  }


  initForm() {
    this.form = this.fb.group({
      dueAt: [this.reviewConfiguration.conferenceDateDue.toDate(), [Validators.required]],
      timeZone: [this.tzService.browserTimeZone]
    });
  }

  /** Whether the number of selected elements matches the total number of rows. */
  claimIsAllSelected() {
    const numSelected = this.claimSelection.selected.length;
    const numRows = this.userClaimsDataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  claimMasterToggle({ checked }) {
    !checked ?
      this.claimSelection.clear() :
      this.userClaimsDataSource.data.forEach(row => {
        if (!this.submission.isReviewer(row.id)) {
          this.claimSelection.select(row);
        }
      }
    );
  }

  /** Whether the number of selected elements matches the total number of rows. */
  findbyInterestIsAllSelected() {
    const numSelected = this.findSelection.selected.length;
    const numRows = this.usersByInterestDataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  findByInterestMasterToggle({ checked }) {
    !checked ?
      this.findSelection.clear() :
      this.usersByInterestDataSource.data.forEach(row => {
        if (!this.submission.isReviewer(row.id)) {
          this.findSelection.select(row);
        }
      }
    );
  }

  addReview(): void {
    const selected = [...this.claimSelection.selected, ...this.findSelection.selected];

    forkJoin(selected.map(user => (this.saveReview(this.infoService.user, user, this.submission.id, this.form.value.dueAt))))
    .subscribe(reviews => {
      if (reviews.length > 1) {
        this.notificationService.notify('messages.assigned-multiple-user-reviews', { params: { users: reviews.length } });
      } else {
        this.notificationService.notify('messages.assigned-user-review', { params: { user: reviews[0].userName } });
      }
      // Como DIÁLOGO: fecha devolvendo as revisões criadas (o opener atualiza a
      // lista). Como PÁGINA: recarrega as tabelas.
      if (this.dialogRef) {
        this.dialogRef.close(reviews);
      } else {
        this.getReviewers();
      }
    }, error => {
      this.notificationService.notifyError('errors.could-not-create-review');
    });
  }

  saveReview(assignedBy: User, user: User, submission: number, dueAt): Observable<Review> {
    const review = new AssignReview();
    review.assignedBy = assignedBy.id;
    review.user = user.id;
    review.dueAt = dueAt;
    review.submission = submission;

    return this.reviewService.createReview(review);
  }

  clearSearchInput(): void {
    this.form.get('dueAt').setValue(this.reviewConfiguration.conferenceDateDue.toDate());
  }

  today(): Date {
    return new Date();
  }

  joinTopics(user: { topics?: EventTopic[] }): string {
    return this.topicsFor(user).join(', ');
  }

  // Tópicos do usuário que interseccionam os da submissão (p/ chips).
  topicsFor(user: { topics?: EventTopic[] }): string[] {
    if (!user || !user.topics || !this.submission || !this.submission.topics) { return []; }
    return this.submission.topics
      .filter(t => user.topics.find(topic => topic.id === t.id))
      .map(t => t.name);
  }

  // Afiliação vem do backend com CAIXA inconsistente (tudo maiúsculo ou misto).
  // Normaliza p/ title-case preservando siglas curtas (USP, UFC), tokens já
  // mistos (UnB/FGA, "Federal University…") e conectores (de/do/da/of…).
  formatAffiliation(name: string): string {
    if (!name) { return ''; }
    const connectors = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'of', 'the', 'for', 'and', 'a', 'à']);
    return name.trim().split(/\s+/).map((w, i) => {
      if (w.indexOf('/') >= 0) { return w; }                       // ex.: UnB/FGA
      if (/[a-zà-ÿ]/.test(w) && /[A-ZÀ-Ý]/.test(w)) { return w; }  // já misto (ex.: Federal)
      const lower = w.toLowerCase();
      if (i > 0 && connectors.has(lower)) { return lower; }
      if (w.length <= 4 && w === w.toUpperCase()) { return w; }    // sigla curta (USP, UFC, PUC)
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }).join(' ');
  }

  // "(sigla Afiliação, País)" — mesmo padrão do resto do sistema (paper-info).
  // Usa a sigla real quando existe; senão o nome normalizado. País via CountryPipe.
  formatUserAffiliation(user: any): string {
    const profile = user && user.profile;
    if (!profile) { return ''; }
    const aff = profile.affiliation;
    let label = '';
    if (aff) {
      label = (aff.acronym && aff.acronym !== aff.name) ? aff.acronym : this.formatAffiliation(aff.name);
    }
    const country = profile.country ? this.countryPipe.transform(profile.country) : '';
    const parts = [label, country].filter(v => !!v);
    return parts.length ? `(${parts.join(', ')})` : '';
  }

  // Ordenação por coluna, por tabela ('claim' | 'interest'). Clique alterna asc/desc.
  public sort = { which: '', key: '', dir: 1 };
  sortTable(which: 'claim' | 'interest', key: string): void {
    const ds = which === 'claim' ? this.userClaimsDataSource : this.usersByInterestDataSource;
    const dir = (this.sort.which === which && this.sort.key === key) ? -this.sort.dir : 1;
    this.sort = { which, key, dir };
    ds.data = [...ds.data].sort((a: any, b: any) => {
      const va = a[key], vb = b[key];
      if (typeof va === 'number' && typeof vb === 'number') { return (va - vb) * dir; }
      return String(va == null ? '' : va).localeCompare(String(vb == null ? '' : vb), 'pt') * dir;
    });
  }
  sortIcon(which: string, key: string): string {
    if (this.sort.which !== which || this.sort.key !== key) { return 'unfold_more'; }
    return this.sort.dir === 1 ? 'arrow_upward' : 'arrow_downward';
  }
}
